import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import PageTitle from "../../../components/partials/PageTitle";
import {
  addStaffPersonalInfo,
  fetchAllStates,
  resetStaffStatus,
} from "../../../redux/slice/staff/staffSlice";
import { isEmptyObject } from "../../../utils/validators/validators";
import Form from "./../../../components/form/Form";
import InputDate from "./../../../components/form/inputs/datePicker";
import useFileSelected from "./../../../utils/hooks/useFileSelected";
import useInputChange from "./../../../utils/hooks/useInputChange";

const AddStaff = () => {
  const [error, setError] = useState({});

  // initialize form state
  const [staffInputData, setStaffInputData] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    state: "",
    l_g_a: "",
    religion: "",
    gender: "",
    d_o_b: "",
    marital_status: "",
    language_spoken: "",
    image: {},
    temp_url: "",
  });
  const [localgovernment, setLocalgovernment] = useState([]);
  const [input, handleChange] = useInputChange(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );
  const [fileInput, fileChangeHandler] = useFileSelected(
    staffInputData,
    setStaffInputData,
    error,
    setError
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const passportRef = useRef();

  // fetch records from redux state
  const { user_permissions } = useSelector((state) => state.auth?.user);
  const {
    isLoading,
    isError,
    isSuccess,
    app_id,
    staff_records,
    allStates,
    pages,
  } = useSelector((state) => state.staff);
  const { nextPage } = useOutletContext();

  // submit form
  const handleSubmit = () => {
    if (isEmptyObject(error)) {
      dispatch(addStaffPersonalInfo(staffInputData));
    }
  };

  // breadcrumb
  const breadcrumb = {
    from: {
      title: "Staff",
      link: "/admin/add-staff",
    },
    current: {
      title: "onboarding",
      link: null,
    },
  };

  // Redirect if application is in progress
  useEffect(() => {
    app_id && nextPage && navigate(nextPage);
  }, [pages]);

  // Fetching all states
  useEffect(() => {
    dispatch(fetchAllStates());
  }, []);

  // navigate to next page if record is creared successfully
  useEffect(() => {
    if (isSuccess && app_id) {
      navigate("/admin/add-staff/contact-information", {
        state: { from: location },
        replace: true,
      });
    }
    return () => {
      dispatch(resetStaffStatus());
    };
  }, [staff_records, isError, isSuccess, isLoading]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "staff module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "create staff"
      )
    ) {
      navigate("/admin/dashboard");
    }
  }, [navigate, user_permissions]);

  return (
    <>
      {/* page title */}
      <PageTitle title='Staff Creation' breadcrumb={breadcrumb} />
      {/* end page title */}

      {/* content */}
      <div className='row'>
        <div className='col-md-12'>
          <div className='card'>
            <div className='card-body'>
              <div className='row pb-2 mb-3 text-capitalize border-bottom'>
                <div className='col-md-12 d-flex justify-content-between align-items-center'>
                  <h4 className='header-title text-uppercase mb-0 text-primary'>
                    Personal Information
                  </h4>
                </div>
              </div>
              <Form
                setError={setError}
                submitHandler={handleSubmit}
                values={staffInputData}
              >
                <div className='row g-'>
                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      First Name <span className='text-danger'>*</span>
                    </label>
                    <input
                      min_length='2'
                      textname='first name'
                      name='first_name'
                      onChange={handleChange}
                      value={input.first_name}
                      type='text'
                      className={`form-control ${
                        error.first_name && "is-invalid"
                      }`}
                      id='inputEmail4'
                      placeholder='First Name'
                    />
                    {error.first_name && (
                      <span className='invalid-feedback'>
                        {error.first_name}
                      </span>
                    )}
                  </div>

                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      Last Name <span className='text-danger'>*</span>
                    </label>
                    <input
                      min_length='2'
                      textname='last name'
                      name='last_name'
                      onChange={handleChange}
                      value={input.last_name}
                      type='text'
                      className={`form-control ${
                        error.last_name && "is-invalid"
                      }`}
                      id='inputEmail4'
                      placeholder='Last Name'
                    />
                    {error.last_name && (
                      <span className='invalid-feedback'>
                        {error.last_name}
                      </span>
                    )}
                  </div>

                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      Middle Name
                    </label>
                    <input
                      validate='false'
                      min_length={input.middle_name && 2}
                      textname='middle name'
                      name='middle_name'
                      onChange={handleChange}
                      value={input.middle_name}
                      type='text'
                      className={`form-control ${
                        error.middle_name && "is-invalid"
                      }`}
                      id='inputEmail4'
                      placeholder='Middle Name'
                    />
                    {error.middle_name && (
                      <span className='invalid-feedback'>
                        {error.middle_name}
                      </span>
                    )}
                  </div>
                </div>

                <div className='row g-2'>
                  <div className='mb-3 mt-3'>
                    <label htmlFor='state' className='form-label'>
                      State <span className='text-danger'>*</span>
                    </label>
                    <select
                      textname='state'
                      name='state'
                      value={input.state}
                      id='state'
                      className={`form-select ${error.state && "is-invalid"}`}
                      onChange={(e) => {
                        setStaffInputData({
                          ...staffInputData,
                          state: e.target.value,
                        });
                        setError((current) => {
                          const copy = { ...current };
                          delete copy["state"];
                          return copy;
                        });
                        setLocalgovernment(() => {
                          return allStates.filter((state) => {
                            return state.name === e.target.value;
                          });
                        });
                      }}
                    >
                      <option value=''>Choose state</option>
                      {allStates &&
                        allStates.map(({ id, name }) => (
                          <option key={id} value={name}>
                            {name}
                          </option>
                        ))}
                    </select>
                    {error.state && (
                      <span className='invalid-feedback'>{error.state}</span>
                    )}
                  </div>
                </div>

                <div className='row g-'>
                  <div className='col-md-6 mb-3'>
                    <label htmlFor='example-textarea' className='form-label'>
                      L.G.A <span className='text-danger'>*</span>
                    </label>
                    <select
                      textname='L.G.A'
                      name='l_g_a'
                      id='l_g_a'
                      value={input.l_g_a}
                      onChange={handleChange}
                      className={`form-select ${error.l_g_a && "is-invalid"}`}
                      disabled={localgovernment?.length === 0 ? true : false}
                    >
                      <option value=''>Choose local government</option>
                      {localgovernment[0]?.lga.map(({ id, name }) => (
                        <option key={id} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    {error.l_g_a && (
                      <span className='invalid-feedback'>{error.l_g_a}</span>
                    )}
                  </div>
                  <div className='col-md-6 mb-3'>
                    <label htmlFor='example-textarea' className='form-label'>
                      Religion <span className='text-danger'>*</span>
                    </label>
                    <select
                      textname='religion'
                      name='religion'
                      id='religion'
                      value={input.religion}
                      onChange={handleChange}
                      className={`form-select ${
                        error.religion && "is-invalid"
                      }`}
                    >
                      <option value=''>Choose religion</option>
                      <option value='Christainity'>Christainity</option>
                      <option value='Islam'>Islam</option>
                      <option value='Traditional'>Traditional</option>
                      <option value='Others'>Others</option>
                    </select>
                    {error.religion && (
                      <span className='invalid-feedback'>{error.religion}</span>
                    )}
                  </div>
                </div>

                <div className='row g- mb-3'>
                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      Gender <span className='text-danger'>*</span>
                    </label>
                    <select
                      textname='gender'
                      name='gender'
                      id='gender'
                      value={input.gender}
                      onChange={handleChange}
                      className={`form-select ${error.gender && "is-invalid"}`}
                    >
                      <option value=''>Choose gender</option>
                      <option value='Male'>Male</option>
                      <option value='Female'>Female</option>
                    </select>
                    {error.gender && (
                      <span className='invalid-feedback'>{error.gender}</span>
                    )}
                  </div>

                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      Date of Birth <span className='text-danger'>*</span>
                    </label>
                    {/* <input type="date"
                                        className='form-control'
                                      /> */}
                    <InputDate
                      name='d_o_b'
                      textname='date of birth'
                      className={`form-select ${error.d_o_b && "is-invalid"}`}
                      state={staffInputData}
                      setState={setStaffInputData}
                      error={error}
                      setError={setError}
                      // onChange
                    />

                    {error.d_o_b && (
                      <span className='d-block mt-1 fs-6 text-danger'>
                        Please enter date of birth
                      </span>
                    )}
                  </div>

                  <div className='mb-0 col-md-4'>
                    <label htmlFor='inputEmail4' className='form-label'>
                      Marital Status <span className='text-danger'>*</span>
                    </label>
                    <select
                      textname='marital status'
                      name='marital_status'
                      id='marita_status'
                      value={input.marital_status}
                      onChange={handleChange}
                      className={`form-select ${
                        error.marital_status && "is-invalid"
                      }`}
                    >
                      <option value=''>Choose marital status</option>
                      <option value='Single'>Single</option>
                      <option value='Married'>Married</option>
                      <option value='Divorced'>Divorced</option>
                    </select>
                    {error.marital_status && (
                      <span className='invalid-feedback'>
                        {error.marital_status}
                      </span>
                    )}
                  </div>
                </div>

                <div className='row g- mb-3'>
                  <label htmlFor='example-textarea' className='form-label'>
                    Language(s) Spoken
                  </label>
                  <input
                    minLength={input.language_spoken && 3}
                    validate='false'
                    textname='language spoken'
                    name='language_spoken'
                    value={input.language_spoken}
                    onChange={handleChange}
                    type='text'
                    className={`form-control ${
                      error.language_spoken && "is-invalid"
                    }`}
                    placeholder='Language Spoken'
                  />
                  {error.language_spoken && (
                    <span className='invalid-feedback'>
                      {error.language_spoken}
                    </span>
                  )}
                </div>

                <div className='row g-2'>
                  <div className='passport-container d-flex gap-4'>
                    <div className='input-wrapper'>
                      <label
                        className='btn btn-primary rounded-0'
                        htmlFor='passport'
                        onClick={() => {
                          passportRef.current.click();
                        }}
                      >
                        Choose Passport
                      </label>
                      <input
                        textname='passport'
                        className='d-none'
                        accept='image/*'
                        type='file'
                        name='image'
                        // value={input?.image}
                        onChange={fileChangeHandler}
                        ref={passportRef}
                      />
                      {error.image && (
                        <span className='d-block text-danger mt-2'>
                          {error.image}
                        </span>
                      )}
                    </div>
                    <div className='passport'>
                      <img
                        src={
                          staffInputData?.temp_url && staffInputData?.temp_url
                        }
                        width='100'
                        alt=''
                      />
                    </div>
                  </div>
                </div>

                <div className='float-end'>
                  <button
                    disabled={isLoading && true}
                    className='btn btn-primary w-10'
                  >
                    Proceed
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* end content */}
    </>
  );
};

export default AddStaff;
